import { useState } from "react";
import { LABELS } from "@match/shared";
import { api, errorMessage } from "@/lib/api";
import { Button, useInMenu } from "@/components/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useRefreshDirectory } from "@/lib/queries";
import { lots, money } from "@/lib/format";
import { showNotice } from "@/components/notice";
import type { DeletionPlan, DeletionResult, DeletionScope } from "@/lib/types";

/**
 * Le entita' che si cancellano, e dove chiedere.
 *
 * La mappa sta qui e non nei chiamanti: una pagina che costruisse da se' l'URL
 * dell'anteprima e quello della cancellazione potrebbe sbagliarne uno, e i due
 * devono parlare della stessa riga.
 */
const ROTTE = {
  cliente: (id: string) => `/clients/${id}`,
  conto: (id: string) => `/accounts/${id}`,
  fase: (id: string) => `/sessions/${id}`,
  ciclo: (id: string) => `/cycles/${id}`,
  server: (id: string) => `/servers/${id}`,
  // Firm e broker stanno nella stessa tabella: la rotta e' la stessa.
  firm: (id: string) => `/prop-firms/${id}`,
  broker: (id: string) => `/prop-firms/${id}`,
  programma: (id: string) => `/presets/${id}`,
} as const;

export type DeletableKind = keyof typeof ROTTE | "simbolo";

/**
 * Cliente, conto, ciclo e fase non si cancellano: si timbrano, si provano a
 * chiudere le posizioni a mercato, e si ripristinano dagli Eliminati. Simbolo,
 * firm, broker e programma restano cancellazioni vere, con i loro blocchi.
 */
const SI_TIMBRA = new Set<DeletableKind>(["cliente", "conto", "ciclo", "fase"]);

const plurale = (n: number, uno: string, molti: string) => (n === 1 ? `1 ${uno}` : `${n} ${molti}`);

/**
 * L'esito, solo se c'e' qualcosa da dire: posizioni chiuse a mercato o rimaste
 * aperte. Un'eliminazione senza niente a mercato non dice niente — la riga
 * sparita dalla lista e' gia' l'esito.
 */
function avvisaEsito(label: string, esito: DeletionResult) {
  if (!esito.closures.length) return;
  const rimaste = esito.closures.filter((c) => c.esito === "RIMASTA_APERTA");
  showNotice({
    tone: rimaste.length ? "alert" : "info",
    title: rimaste.length
      ? `Eliminato ${label}, ma ${rimaste.length === 1 ? "1 posizione è rimasta aperta" : `${rimaste.length} posizioni sono rimaste aperte`} a mercato: evento critico aperto.`
      : `Eliminato ${label}: ${esito.closures.length === 1 ? "1 posizione chiusa" : `${esito.closures.length} posizioni chiuse`} a mercato.`,
    lines: esito.closures.map(
      (c) =>
        `${c.accountLabel} · ${c.seq === null ? "operazione singola" : `#${c.seq}`} · ${c.symbol} ${lots(c.lots)} lotti · ticket ${c.ticket}: ` +
        (c.esito === "CHIUSA" ? "chiusa" : `rimasta aperta${c.motivo ? ` (${c.motivo})` : ""}`),
    ),
  });
}

/** L'unica riga di conseguenze sotto il titolo, quando non c'e' un elenco da mostrare. */
function rigaConseguenze(kind: DeletableKind, counts: DeletionScope["counts"] | null): string {
  const con = counts
    ? [counts.cycles && plurale(counts.cycles, "ciclo", "cicli"), counts.sessions && plurale(counts.sessions, "fase", "fasi"), counts.pairs && plurale(counts.pairs, "operazione", "operazioni")].filter(Boolean)
    : [];
  const insieme = con.length ? ` con ${con.join(", ")}` : "";
  if (kind === "cliente") {
    const conti = counts?.accounts ? ` con ${plurale(counts.accounts, "conto non prop", "conti non prop")}, scollegati` : "";
    return `Sparisce${conti}; lo storico resta e l'accesso si disattiva. Si ripristina da Eliminati, se l'email non è stata ripresa.`;
  }
  if (kind === "conto") return `Si scollega e sparisce${insieme}. Si ripristina da Eliminati, se il numero MT5 non è stato ripreso.`;
  if (kind === "ciclo" || kind === "fase") return `Sparisce${insieme}. Si ripristina da Eliminati.`;
  return "Si cancella e non si ripristina.";
}

/**
 * ELIMINA DEFINITIVAMENTE, con la conseguenza dichiarata PRIMA.
 *
 * Non «attenzione, azione irreversibile»: **la frase che dice che cosa succede a
 * questo dato**, coi numeri dentro, presa dal backend e non riscritta qui. Se la
 * frase la scrivesse l'interfaccia, il giorno in cui la regola cambia
 * l'interfaccia direbbe una cosa e il backend ne farebbe un'altra.
 *
 * Tre stati, e il primo è quello che conta:
 *
 *  1. **non si è ancora chiesto**: solo il pulsante. L'anteprima costa una
 *     lettura e una domanda al gateway — non si fa a ogni riga di una tabella,
 *     si fa quando qualcuno ci pensa davvero;
 *  2. **si è chiesto e non si può**: i motivi, uno per uno. Non «impossibile»;
 *  3. **si è chiesto e si può**: la frase, le conseguenze, e la conferma.
 *
 * È diverso da «Archivia», che sta accanto: archiviare conserva i riferimenti,
 * questa li toglie di mezzo. Serve per le righe nate per errore — e serve
 * perché archiviarle **non libererebbe lo slot unico**: un conto archiviato
 * tiene il suo numero MT5, e quel numero non si può reinserire.
 */
export function DeleteForever({
  kind,
  id,
  label,
  size,
  serverId,
  onDeleted,
}: {
  kind: DeletableKind;
  id: string;
  label: string;
  size?: "sm";
  /** Solo per `simbolo`: la mappatura vive sotto il suo server. */
  serverId?: string;
  onDeleted?: () => void;
}) {
  const refresh = useRefreshDirectory();
  const qc = useQueryClient();
  const [plan, setPlan] = useState<DeletionPlan | DeletionScope | null>(null);
  const [capito, setCapito] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inMenu = useInMenu();

  const base = kind === "simbolo" ? `/servers/${serverId}/symbols/${id}` : ROTTE[kind](id);
  // Il simbolo si cancella sulla sua stessa rotta; le altre entita' hanno un
  // `/permanent` che le distingue dall'archiviazione.
  const urlCancella = kind === "simbolo" ? base : `${base}/permanent`;

  const chiedi = async () => {
    setBusy(true);
    setError(null);
    try {
      setCapito(false);
      setPlan(await api.deletionPreview<DeletionPlan | DeletionScope>(`${base}/deletion-preview`));
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  /*
   * AL SUCCESSO LA FINESTRA SI CHIUDE DA SOLA e le liste si ricaricano subito.
   * L'esito, se c'e' qualcosa da dire, va in un avviso che si chiude da solo
   * (`components/notice.tsx`): qui non potrebbe restare, perche' la riga che
   * contiene questo riquadro sparisce con la ricarica. Un rifiuto invece resta
   * qui sotto: la finestra non ha finito il suo lavoro.
   */
  /** `withSession`: la risposta alla domanda sulla fase non chiusa; assente quando la domanda non c'e'. */
  const cancella = async (withSession?: boolean) => {
    setBusy(true);
    setError(null);
    try {
      const url = withSession === undefined ? urlCancella : `${urlCancella}?withSession=${withSession}`;
      const esito = await api.deleteAt<DeletionResult | { ok: true }>(url);
      setPlan(null);
      await Promise.all([refresh(), qc.invalidateQueries({ queryKey: ["deleted"] }), qc.invalidateQueries({ queryKey: ["archived"] })]);
      if (SI_TIMBRA.has(kind) && "closures" in esito) avvisaEsito(label, esito);
      onDeleted?.();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  if (!plan) {
    return (
      <span className={inMenu ? "flex w-full flex-col items-stretch gap-1" : "inline-flex items-center gap-2"}>
        <Button size={size} variant="ghost" disabled={busy} onClick={() => void chiedi()}>
          {busy ? "Verifico…" : "Elimina"}
        </Button>
        {error && (
          <span role="alert" className="text-xs text-loss">
            {error}
          </span>
        )}
      </span>
    );
  }

  const bloccato = "blockers" in plan && plan.blockers.length > 0;
  const aMercato = "openLegs" in plan ? plan.openLegs : [];
  /*
   * LA DOMANDA SULLA FASE NON CHIUSA (30/09): «Si'» elimina anche la fase (la
   * cascata di sempre, quella descritta qui sopra), «No» solo il conto — e c'e'
   * solo per il broker. L'anteprima e' quella del «Si'».
   */
  const domanda = "sessionChoice" in plan ? (plan.sessionChoice ?? null) : null;
  const conCoppieAperte = domanda?.sessions.some((f) => f.openPairs > 0) ?? false;
  const motivoNoBloccato = domanda?.keepBlocker ?? (conCoppieAperte ? "Chiudi prima l'operazione: la fase ha una coppia aperta." : null);
  // Conto, ciclo e fase: il backend manda anche la loro etichetta piena (per il conto, login e server).
  const nome = "openLegs" in plan ? plan.label : label;
  // Le posizioni a mercato hanno il loro elenco sotto: qui non si ripetono.
  const elenco = plan.consequences.filter((c) => c.code !== "WILL_CLOSE");

  // Eliminando una Fase il ciclo resta e i suoi numeri si rifanno: il prima e il dopo (`CYCLE_RECALC`), solo se cambiano.
  const impatto = "cycleImpact" in plan ? plan.cycleImpact : null;
  const daRecuperare =
    impatto && !impatto.frozen && Math.abs(impatto.after.recoverAmount - impatto.before.recoverAmount) > 0.005
      ? `Da recuperare: ${money(impatto.before.recoverAmount, impatto.currency)} → ${money(impatto.after.recoverAmount, impatto.currency)}`
      : null;

  return (
    <div className="rounded border border-line bg-raised/40 p-3 text-left">
      <p className="text-sm font-medium text-ink">{bloccato ? `Non si può eliminare ${nome}` : `Eliminare ${nome}?`}</p>

      {bloccato ? (
        <ul className="mt-1.5 space-y-0.5">
          {("blockers" in plan ? plan.blockers : []).map((b) => (
            <li key={b.code} className="text-xs text-loss">
              {b.message}
            </li>
          ))}
        </ul>
      ) : SI_TIMBRA.has(kind) || elenco.length === 0 ? (
        <>
          <p className="mt-1 text-xs text-muted">{rigaConseguenze(kind, "counts" in plan ? plan.counts : null)}</p>
          {daRecuperare && (
            <ul className="mt-1.5 space-y-0.5">
              <li className="text-xs text-muted">{daRecuperare}</li>
            </ul>
          )}
        </>
      ) : (
        <ul className="mt-1.5 space-y-0.5">
          {elenco.map((c) => (
            <li key={c.code} className="text-xs text-muted">
              {c.label}
            </li>
          ))}
        </ul>
      )}

      {aMercato.length > 0 && (
        <div className="mt-2 rounded border border-warn/50 p-2">
          <ul className="space-y-0.5">
            {aMercato.map((l) => (
              <li key={l.legId} className="text-xs text-ink">
                {l.accountLabel} · #{l.seq} · {l.symbol} {lots(l.lots)} lotti · ticket <span className="font-mono">{l.ticket}</span>
              </li>
            ))}
          </ul>
          <label className="mt-2 flex items-center gap-2 text-xs text-ink">
            <input type="checkbox" checked={capito} disabled={busy} onChange={(e) => setCapito(e.target.checked)} />
            Ho capito: si prova a chiuderle a mercato
          </label>
        </div>
      )}

      {!bloccato && domanda && (
        <div className="mt-2 rounded border border-line p-2">
          <p className="text-sm text-ink">
            Questo conto è nella fase {domanda.sessions.map((f) => f.label).join(", ")}. Eliminare anche la fase?
          </p>
          <p className="mt-1 text-xs text-muted">
            {domanda.role === "BROKER"
              ? `«Sì»: si eliminano conto e fase, il prop torna libero. «No»: si elimina solo il conto, e la fase resta «${LABELS.sessionState.AWAITING_BROKER}»; con la contabilità il periodo si chiude sul saldo letto adesso.`
              : "«Sì»: si eliminano conto e fase, il broker torna libero. Senza il prop la fase non esiste, quindi non c'è un «No»."}
          </p>
          {domanda.role === "BROKER" && motivoNoBloccato && <p className="mt-1 text-xs text-warn">{motivoNoBloccato}</p>}
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {!bloccato && !domanda && (
          <Button size="sm" variant="danger" disabled={busy || (aMercato.length > 0 && !capito)} onClick={() => void cancella()}>
            {busy ? "Elimino…" : `Elimina ${label}`}
          </Button>
        )}
        {!bloccato && domanda && (
          <>
            <Button size="sm" variant="danger" disabled={busy || (aMercato.length > 0 && !capito)} onClick={() => void cancella(true)}>
              {busy ? "Elimino…" : "Sì, anche la fase"}
            </Button>
            {domanda.role === "BROKER" && (
              <Button size="sm" disabled={busy || !!motivoNoBloccato} onClick={() => void cancella(false)}>
                No, solo il conto
              </Button>
            )}
          </>
        )}
        <Button size="sm" variant="ghost" disabled={busy} onClick={() => setPlan(null)}>
          {bloccato ? "Chiudi" : "Annulla"}
        </Button>
      </div>

      {error && (
        <p role="alert" className="mt-2 text-xs text-loss">
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * ARCHIVIA un conto. **La meta' conservativa della scelta.**
 *
 * La rotta c'era dal primo giorno e **nessun componente la chiamava**: una
 * funzione che esiste e che nessuno puo' usare, esattamente come il «Ripristina»
 * prima del passo 16.
 *
 * E da quando accanto c'e' «Elimina» e' peggio: l'operatore vedrebbe **solo
 * l'opzione distruttiva** di una coppia in cui quella conservativa esiste ma
 * passa solo dall'API. Sono le due meta' di una scelta, e mostrarne una sola la
 * fa sembrare l'unica.
 *
 * L'archiviazione non chiede un'anteprima: non c'e' niente da dichiarare —
 * niente sparisce, e si torna indietro dalla sezione «Archiviati». Il backend
 * rifiuta comunque se ci sono soldi a mercato (`marketBlocker`), e quel rifiuto
 * si mostra com'e' arrivato.
 */
export function ArchiveAccount({ id, label, size }: { id: string; label: string; size?: "sm" }) {
  const refresh = useRefreshDirectory();
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inMenu = useInMenu();

  const archivia = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.accounts.archive(id);
      setConfirm(false);
      await refresh();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <span className={inMenu ? "flex w-full flex-col items-stretch gap-1" : "inline-flex flex-wrap items-center gap-2"}>
      {confirm ? (
        <>
          <Button size={size} variant="danger" disabled={busy} onClick={() => void archivia()}>
            {busy ? "Archivio…" : `Conferma: archivia ${label}`}
          </Button>
          <Button size={size} variant="ghost" disabled={busy} onClick={() => setConfirm(false)}>
            Annulla
          </Button>
          <span className="text-xs text-faint">Sparisce dagli elenchi, non dal database: si ripristina da Clienti.</span>
        </>
      ) : (
        <Button size={size} variant="ghost" onClick={() => setConfirm(true)}>
          Archivia
        </Button>
      )}
      {error && (
        <span role="alert" className="text-xs text-loss">
          {error}
        </span>
      )}
    </span>
  );
}
